import { SYSTEM_ROLES } from "constants/auth";

/**
 * Checks if a user is a privileged user based on their role
 * @param {object} user - The user object to check
 * @returns {boolean} True if user is a privileged user
 */
export function isPrivilegedUser(user) {
  return isSuperAdmin(user) || isDeveloper(user);
}

/**
 * Checks if a user has Super Admin role
 * @param {object} user - The user object to check
 * @returns {boolean} True if user is a super admin
 */
export function isSuperAdmin(user) {
  return hasRole(user, SYSTEM_ROLES.SUPER_ADMIN, "exact");
}

/**
 * Checks if a user has Brand Dashboard Manager role based on specified matching strategy
 * @param {object} user - The user object to check
 * @param {RoleMatchStrategy} [strategy='both'] - How to match the role:
 *   - 'exact': Only match the exact role ID
 *   - 'parent': Only match parent role
 *   - 'both': Match either role ID or parent (default)
 * @returns {boolean} True if user matches the brand dashboard manager role criteria
 */
export function isBrandDashboardManager(user, strategy = "both") {
  return hasRole(user, SYSTEM_ROLES.BRAND_DASHBOARD_MANAGER, strategy);
}

/**
 * Checks if a user has Dashboard Manager role based on specified matching strategy
 * @param {object} user - The user object to check
 * @param {RoleMatchStrategy} [strategy='both'] - How to match the role:
 *   - 'exact': Only match the exact role ID
 *   - 'parent': Only match parent role
 *   - 'both': Match either role ID or parent (default)
 * @returns {boolean} True if user matches the dashboard manager role criteria
 */
export function isDashboardManager(user, strategy = "both") {
  return hasRole(user, SYSTEM_ROLES.DASHBOARD_MANAGER, strategy);
}

/**
 * Checks if a user has Dashboard Agent role based on specified matching strategy
 * @param {object} user - The user object to check
 * @param {RoleMatchStrategy} [strategy='both'] - How to match the role:
 *   - 'exact': Only match the exact role ID
 *   - 'parent': Only match parent role
 *   - 'both': Match either role ID or parent (default)
 * @returns {boolean} True if user matches the dashboard agent role criteria
 */
export function isDashboardAgent(user, strategy = "both") {
  return hasRole(user, SYSTEM_ROLES.DASHBOARD_AGENT, strategy);
}

/**
 * Checks if a user has Developer role
 * @param {object} user - The user object to check
 * @returns {boolean} True if user is a developer
 */
export function isDeveloper(user) {
  return hasRole(user, SYSTEM_ROLES.DEV, "exact");
}

/**
 * Checks if a user has Member role based on specified matching strategy
 * @param {object} user - The user object to check
 * @param {RoleMatchStrategy} [strategy='both'] - How to match the role:
 *   - 'exact': Only match the exact role ID
 *   - 'parent': Only match parent role
 *   - 'both': Match either role ID or parent (default)
 * @returns {boolean} True if user matches the member role criteria
 */
export function isMember(user, strategy = "both") {
  return hasRole(user, SYSTEM_ROLES.MEMBER, strategy);
}

/**
 * Checks if a user has a specific permission
 * @param {object} user - The user object to check
 * @param {string} permission - The permission to check
 * @returns {boolean} True if user has the permission
 */
export function hasPermission(user, permission) {
  return user.currentRoleModules.includes(permission);
}

/**
 * Checks if a user has all specified permissions
 * @param {object} user - The user object to check
 * @param {string[]} permissions - The permissions to check
 * @returns {boolean} True if user has all specified permissions
 */
export function hasAllPermissions(user, permissions) {
  return permissions.every((permission) => hasPermission(user, permission));
}

/**
 * Checks if a user has any of the specified permissions
 * @param {object} user - The user object to check
 * @param {string[]} permissions - The permissions to check
 * @returns {boolean} True if user has any of the specified permissions
 */
export function hasAnyPermission(user, permissions) {
  return permissions.some((permission) => hasPermission(user, permission));
}

/**
 * Checks if a user has admin access or all specified permissions
 * @param {object} user - The user object to check
 * @param {string[]} permissions - The permissions to check
 * @returns {boolean} True if user has admin access or all specified permissions
 */
export function hasAdminAccessOrPermissions(user, permissions) {
  // If permissions array is empty, only allow privileged users
  if (permissions.length === 0) {
    return isPrivilegedUser(user);
  }

  // Otherwise, allow privileged users OR users with all specified permissions
  return isPrivilegedUser(user) || hasAllPermissions(user, permissions);
}

/**
 * Checks if a user has admin access or any of the specified permissions
 * @param {object} user - The user object to check
 * @param {string[]} permissions - The permissions to check
 * @returns {boolean} True if user has admin access or any of the specified permissions
 */
export function hasAdminAccessOrAnyPermission(user, permissions) {
  if (permissions.length === 0) {
    return isPrivilegedUser(user);
  }
  return isPrivilegedUser(user) || hasAnyPermission(user, permissions);
}

/**
 * @typedef {'exact' | 'parent' | 'both'} RoleMatchStrategy
 */

/**
 * Generic helper to check if a user has a specific role based on matching strategy
 * @param {Object} user - The user object to check
 * @param {string} roleId - The role ID to check against
 * @param {RoleMatchStrategy} [strategy='both'] - How to match the role:
 *   - 'exact': Only match the exact role ID
 *   - 'parent': Only match parent role
 *   - 'both': Match either role ID or parent (default)
 * @returns {boolean} True if user matches the role criteria
 */
function hasRole(user, roleId, strategy = "both") {
  const exactMatch = user?.role?.id === roleId;
  const parentMatch = user?.role?.parent === roleId;

  switch (strategy) {
    case "exact":
      return exactMatch;
    case "parent":
      return parentMatch;
    case "both":
      return exactMatch || parentMatch;
    default: {
      throw new Error(
        `Invalid strategy: ${strategy}. Valid strategies are: exact, parent, both`,
      );
    }
  }
}
